import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from './components/Header';
import trackPageView from './services/trackPageView';
import Footer from './components/Footer';
import Home from './pages/Home';
import ConsularAct from './pages/ConsularAct';
import SearchResults from './pages/SearchResults';
import Payment from './pages/Payment';
import Confirmation from './pages/Confirmation';
import TestDocumentGeneration from './pages/TestDocumentGeneration';
import LegalMentions from './pages/LegalMentions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Admin from './pages/admin';
import Login from './pages/admin/Login';
import ResetPassword from './pages/admin/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Chatbot from './components/Chatbot';

// Utiliser la clé de l'environnement
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function App() {
  const [showChatbot, setShowChatbot] = useState(false);
  const location = useLocation();
  
  // Tracking analytique pour les pages publiques uniquement
  useEffect(() => {
    // Ne pas tracker les pages d'administration
    if (!location.pathname.includes('/admin')) {
      trackPageView('pageview', { 
        page: location.pathname,
        isPublic: true
      });
    }
  }, [location.pathname]);
  
  return (
    <div className="w-screen max-w-full overflow-x-hidden min-h-screen flex flex-col">
      <ToastContainer 
        position="top-right" 
        autoClose={5000} 
        hideProgressBar={false} 
        newestOnTop 
        closeOnClick 
        rtl={false} 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover 
        limit={1} 
      />
      <AuthProvider>
        <Elements stripe={stripePromise}>
          <Routes>
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route path="/admin/*" element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            } />
            <Route path="/*" element={
              <>
                <Header />
                <main className="flex-grow">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/actes/:id" element={<ConsularAct />} />
                    <Route path="/search" element={<SearchResults />} />
                    <Route 
                      path="/payment" 
                      element={<Payment />} 
                    />
                    <Route 
                      path="/confirmation" 
                      element={<Confirmation />} 
                    />
                    <Route 
                      path="/test-document" 
                      element={<TestDocumentGeneration />} 
                    />
                    <Route 
                      path="/mentions-legales" 
                      element={<LegalMentions />} 
                    />
                    <Route 
                      path="/politique-de-confidentialite" 
                      element={<PrivacyPolicy />} 
                    />
                  </Routes>
                </main>
                <Footer />
              </>
            } />
          </Routes>
        
          {/* Chatbot Button - Masqué sur les pages d'administration */}
          {!location.pathname.includes('/admin') && (
            <>
              {showChatbot && <Chatbot onClose={() => setShowChatbot(false)} />}
              <div className="fixed bottom-4 right-4 flex items-center space-x-2">
                {!showChatbot && (
                  <div className="bg-white text-green-800 px-4 py-2 rounded-lg shadow-lg text-sm mr-2 animate-fade-in">
                    Besoin d&apos;aide ?
                  </div>
                )}
                <button 
                  onClick={() => setShowChatbot(!showChatbot)}
                  className="bg-green-500 text-white p-3 rounded-full shadow-lg hover:bg-green-600 flex items-center justify-center"
                  aria-label={showChatbot ? "Fermer l'assistant" : "Ouvrir l'assistant"}
                >
                  {showChatbot ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  )}
                </button>
              </div>
            </>
          )}
        </Elements>
      </AuthProvider>
    </div>
  );
}

export default App;
